import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Modal, Form, Spinner, Col, Row, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime } from "luxon";
import { DraggableModalDialog } from './DraggableModalDialog';
import { useTranslationUtils } from '../Utilities/TranslationUtils';
import { DialogSelect } from '../DialogInputs';
import { useGrades, useAlternativeGrades, AlternativeGradeReviewResult, useReviewAlternativeGrades } from '../Utilities/useGrades';
import { Quality } from '../Types/LotDetailQuality';

export interface SelectGradeDialogProps {
    businessUnitCode: string,
    lotNumber: string,
    splitStartUtc: DateTime | null,
    splitEndUtc: DateTime | null,
    qualities: Quality[] | null,
    onClose: (selectedGrade: string | null) => void
};

export const useSelectGradeDialog = () =>
{
    const [showDialog, setShowDialog] = useState(false);

    const show = () => {
        setShowDialog(true);
    }

    const SelectGradeDialog = ({ businessUnitCode, lotNumber, splitStartUtc, splitEndUtc, qualities, onClose }: SelectGradeDialogProps) => {
        const { t } = useTranslation();
        const { ts } = useTranslationUtils();

        const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
        const [selectedOther, setSelectedOther] = useState(false);
        const [formValidated, setFormValidated] = useState(false);
        const { grades, loading: loadingGrades, getGrades } = useGrades(businessUnitCode);
        const { alternativeGrades, loading: loadingAlternativeGrades, getAlternativeGrades } = useAlternativeGrades(lotNumber);

        const { reviewAlternativeGrades, reviewAlternativeGradesForSplitPackageLot } = useReviewAlternativeGrades();

        const [reviewedGrades, setReviewedGrades] = useState<AlternativeGradeReviewResult[]>([]);
        const [loadingReviewedGrades, setLoadingReviewedGrades] = useState(false);

        let loading = loadingGrades || loadingAlternativeGrades;

        useEffect(() => {

            if (showDialog) {
                if (!loadingGrades) {
                    getGrades(businessUnitCode);
                }

                if (!loadingAlternativeGrades && alternativeGrades.length === 0) {
                    getAlternativeGrades(lotNumber);
                }
            }

        }, [showDialog]);

        useEffect(() => {

            if (!loadingAlternativeGrades && !loadingReviewedGrades && alternativeGrades.length > 0 && reviewedGrades.length === 0) {

                setLoadingReviewedGrades(true);

                if (splitStartUtc != null && splitEndUtc != null && qualities != null) {
                    reviewAlternativeGradesForSplitPackageLot(lotNumber, alternativeGrades, splitStartUtc, splitEndUtc, qualities, setReviewedGrades)
                }
                else {
                    reviewAlternativeGrades(lotNumber, alternativeGrades, setReviewedGrades)
                }
            }
        }, [alternativeGrades]);

        const handleSelectGrade = (e: ChangeEvent<HTMLInputElement>) => {

            if (e.target.dataset.id !== "other") {
                setSelectedOther(false);
                setSelectedGrade(e.target.dataset.id ?? null);
            } else {
                setSelectedOther(true);
            }
        };

        const handleSave = (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            e.stopPropagation();

            closeDialogAndResetForm();

            onClose(selectedGrade);
        };

        const closeDialogAndResetForm = () => {
            setShowDialog(false);
        };

        if (!showDialog) {
            return (null);
        }

        return (
            <>
                <Modal dialogAs={DraggableModalDialog} show={showDialog} onHide={closeDialogAndResetForm} backdrop="static">
                    <Modal.Header closeButton>
                        <Modal.Title>{t("Dialogs.SelectGrade.Title")}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {loading &&
                            <div className="d-flex justify-content-center">
                                <Spinner animation="border" />
                            </div>
                        }
                        {!loading &&
                            <Form id="selectGrade" noValidate validated={formValidated} onSubmit={handleSave}>
                                <Form.Group controlId="gradeSelection">
                                    <Form.Group as={Row} controlId="alternativeGradeSelection" className="mb-3">
                                        <Form.Label column xs={4}>{t("Dialogs.SelectGrade.AlternativeGrades")}</Form.Label>
                                        <Col xs={8} className="pt-2">
                                            {
                                                alternativeGrades.map((grade, index) => {

                                                    let reviewedGrade = reviewedGrades.find(r => r.gradeName === grade);

                                                    if (reviewedGrade === undefined) {
                                                        return (
                                                            <Form.Check type="radio" name="grades" key={index} id={`grade-${grade}`} data-id={grade} label={grade} className={`form-radio-input ms-2 mb-3 form-check`} required>
                                                                <Form.Check.Input type="radio" name="grades" onChange={handleSelectGrade} data-id={grade}></Form.Check.Input>
                                                                <Form.Check.Label>{grade} {loadingReviewedGrades && <Spinner animation="border" size="sm" />}</Form.Check.Label>
                                                            </Form.Check>
                                                        );
                                                    }

                                                    if (reviewedGrade.violation) {

                                                        // Create a pop-over containing the review warnings
                                                        let popover = (
                                                            <Popover >
                                                                <Popover.Header as="h3">Warnings</Popover.Header>
                                                                <Popover.Body><ul>
                                                                    {reviewedGrade.warnings.map((warning, i) =>
                                                                        <li key={`block-warning-${i}`}>
                                                                            {ts(warning)}
                                                                        </li>)
                                                                    }
                                                                </ul></Popover.Body>
                                                            </Popover>
                                                        );

                                                        // Had to custom render this to get the overlay trigger to fire off the label. Note, onChange and data-id must be set on the Form.Check.Input
                                                        return (
                                                            <Form.Check type="radio" name="grades" key={index} id={`grade-${grade}`} data-id={grade} label={grade} className={`form-radio-input ms-2 mb-3 form-check text-danger`} required>
                                                                <Form.Check.Input type="radio" name="grades" onChange={handleSelectGrade} data-id={grade}></Form.Check.Input>
                                                                <OverlayTrigger overlay={popover} popperConfig={{}} placement="bottom">
                                                                    <Form.Check.Label>{grade}</Form.Check.Label>
                                                                </OverlayTrigger>
                                                            </Form.Check>
                                                        );
                                                    }

                                                    return (
                                                        <Form.Check type="radio" name="grades" key={index} id={`grade-${grade}`} data-id={grade} label={grade} className='form-radio-input ms-2 mb-3 text-success' onChange={handleSelectGrade} required/>
                                                    );
                                                })
                                            }
                                            <Form.Check
                                                type="radio"
                                                key={999}
                                                name="grades"
                                                id="grade-other"
                                                data-id="other"
                                                label={t("Dialogs.SelectGrade.Other")}
                                                className="form-radio-input ms-2 mb-3"
                                                onChange={handleSelectGrade}
                                                required
                                            />
                                        </Col>
                                    </Form.Group>
                                    {selectedOther &&
                                        <DialogSelect controlId="selectOtherGrade" value={null} error={null} name="otherGrade" title={t("Dialogs.SelectGrade.OtherGrades")} onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedGrade(e.target.value)}>
                                            <option>{t("Dialogs.SelectGrade.SelectGrade")}</option>
                                            {
                                                grades.map((grade) => {
                                                    return <option value={grade}>{grade}</option>;
                                                })
                                            }
                                        </DialogSelect>
                                    }
                                </Form.Group>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeDialogAndResetForm}>
                            {t("Application.Controls.Close")}
                        </Button>
                        <Button variant="primary" type="submit" form="selectGrade" disabled={selectedGrade === null}>
                            {t("Dialogs.SelectGrade.SelectGrade")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }

    return {
        show: show,
        SelectGradeDialog: SelectGradeDialog
    };
}


